import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../../App";
import { toast } from "sonner";
import { Slider } from "../../components/ui/slider";
import {
  ChevronLeft, Save, ChevronDown, ChevronUp, Plus, Minus,
  CheckCircle2, Sparkles, Film, Image, Globe, Layout,
  Palette, Presentation, BookOpen, RectangleHorizontal,
  Table2, HelpCircle, Zap
} from "lucide-react";

const fmt = (n) => "$" + Math.round(n).toLocaleString();

// ── Default Pricing Constants (overridden by backend) ──
const DEFAULT_PRICES = {
  setup_fee: 5500,
  monthly_service_fee: 3500,
  landing_page: 3000,
  full_website: 7500,
  branding: 10000,
  investment_presentation: 4000,
  flipbook: 7500,
  billboard_static: 1500,
  billboard_animated: 3000,
  unit_inventory: 750,
  ai_animated: 899,
  ai_enhance: 599,
  ai_retainer_6: 5394,   // 6 clips/mo × $899
  ai_retainer_12: 9708,  // 12 clips/mo × $899 (10% volume discount)
  ai_retainer_18: 13755, // 18 clips/mo × $899 (15% volume discount)
  cpl_pre_lease_t1: 10,
  cpl_pre_lease_t2: 15,
  cpl_pre_lease_t3: 25,
  cpl_pre_lease_t4: 40,
  cpl_pre_sale_t1: 25,
  cpl_pre_sale_t2: 40,
  cpl_pre_sale_t3: 75,
  cpl_pre_sale_t4: 100,
};

// ── Tier config builders (dynamic from pricing) ──
const buildPreLeaseTiers = (p) => [
  { id: "t1", label: "below $1,500", cpl: p.cpl_pre_lease_t1, sublabel: `CPL Estimate: $${p.cpl_pre_lease_t1}` },
  { id: "t2", label: "$1,500 - $2,500", cpl: p.cpl_pre_lease_t2, sublabel: `CPL Estimate: $${p.cpl_pre_lease_t2}` },
  { id: "t3", label: "$2,500 - $4,500", cpl: p.cpl_pre_lease_t3, sublabel: `CPL Estimate: $${p.cpl_pre_lease_t3}` },
  { id: "t4", label: "$4,500+", cpl: p.cpl_pre_lease_t4, sublabel: `CPL Estimate: $${p.cpl_pre_lease_t4}` },
];
const buildPreSaleTiers = (p) => [
  { id: "t1", label: "below $600K", cpl: p.cpl_pre_sale_t1, sublabel: `CPL Estimate: $${p.cpl_pre_sale_t1}` },
  { id: "t2", label: "$600K - $1.2M", cpl: p.cpl_pre_sale_t2, sublabel: `CPL Estimate: $${p.cpl_pre_sale_t2}` },
  { id: "t3", label: "$1.2M - $2.5M", cpl: p.cpl_pre_sale_t3, sublabel: `CPL Estimate: $${p.cpl_pre_sale_t3}` },
  { id: "t4", label: "$2.5M+", cpl: p.cpl_pre_sale_t4, sublabel: `CPL Estimate: $${p.cpl_pre_sale_t4}` },
];

const HOW_IT_WORKS = [
  {
    step: 1, title: "Setup, Strategy & Initial Ad Content",
    body: "Before any ads go live, we build the foundation properly. We connect all platforms, define the right messaging, and prepare the full funnel so everything is tracked, aligned, and ready to perform once spend is turned on.",
    bullets: [
      "Full setup of Meta, Google Ads, Pixel, Tag Manager, and Analytics",
      "Market research to understand buyer mindset and key selling points",
      "Campaign structure across Meta & Google focused on messaging and intent",
      "Full funnel buildout, including lead capture forms with qualification questions and initial ad creative (static + video)"
    ]
  },
  {
    step: 2, title: "CRM & Lead Management",
    body: "Once leads start coming in, we manage everything inside a centralized CRM so no opportunities are missed and every lead is properly tracked and followed up.",
    bullets: [
      "CRM access during active campaigns",
      "Custom lead qualification forms and fields",
      "Full integration with Meta & Google for lead tracking",
      "Automated email and SMS follow-up sequences",
      "Lead routing, tracking, and visibility across the funnel"
    ]
  },
  {
    step: 3, title: "Ongoing Growth Management",
    body: "Once campaigns are live, we actively manage and optimize performance week over week, focusing on improving lead quality, reducing costs, and scaling what's working.",
    bullets: [
      "Ongoing Meta Ads optimization and performance monitoring",
      "Continuous creative and messaging optimization as needed",
      "Ongoing team support and campaign adjustments",
      "Weekly strategy check-ins as needed based on performance and sales feedback"
    ]
  }
];

// ── Infrastructure service definitions (dynamic) ──
const buildInfraCategories = (p) => [
  {
    id: "digital", label: "Digital Presence", color: "sky",
    items: [
      { id: "landing_page", label: "Landing Page", price: p.landing_page, icon: Layout, type: "toggle", excludes: "full_website",
        desc: "Single high-converting page designed for lead capture and campaign traffic." },
      { id: "full_website", label: "Full Website", price: p.full_website, icon: Globe, type: "toggle", excludes: "landing_page",
        desc: "Multi-page website with full structure, content, and user journey for the development." },
    ]
  },
  {
    id: "branding", label: "Branding & Positioning", color: "violet",
    items: [
      { id: "branding", label: "Branding & Conceptual Package", price: p.branding, icon: Palette, type: "toggle",
        desc: "A complete identity system — positioning, name and messaging, logo, brand deck, one sheet, and location map — delivered as production-ready files." },
      { id: "investment_presentation", label: "Investment Presentation", price: p.investment_presentation, icon: Presentation, type: "toggle",
        subtext: "Up to 20 pages",
        desc: "Investor-facing presentation designed to communicate the vision, opportunity, and financial narrative." },
    ]
  },
  {
    id: "collateral", label: "Sales & Marketing Collateral", color: "amber",
    items: [
      { id: "flipbook", label: "Development Flipbook", price: p.flipbook, icon: BookOpen, type: "toggle",
        subtext: "40-50 pages",
        desc: "Comprehensive project sales and marketing book used for presentations, brokers, and buyers." },
      { id: "billboard", label: "Billboard Design Creation", icon: RectangleHorizontal, type: "dropdown",
        desc: "Billboard-ready creative using project visuals for awareness and traffic.",
        options: [
          { value: "none", label: "None", price: 0 },
          { value: "static", label: "Static", price: p.billboard_static },
          { value: "animated", label: "Animated (up to 10 sec)", price: p.billboard_animated },
        ]},
      { id: "unit_inventory", label: "Unit Inventory Breakdown Sheet", price: p.unit_inventory, icon: Table2, type: "toggle",
        desc: "Clean, structured unit matrix for sales teams and buyers." },
    ]
  },
  {
    id: "ai_content", label: "AI-Enhanced Marketing Content", color: "rose",
    items: [
      { id: "ai_social", label: "AI Social Media Content", icon: Film, type: "retainer",
        subtext: "15-second video clips — AI-assisted, no revisions included",
        desc: "Short-form animated clips generated from existing renderings for use in ads and social media. Monthly retainer packages.",
        tiers: [
          { value: "none", label: "None", clips: 0, monthly: 0 },
          { value: "6", label: "6 clips/mo", clips: 6, monthly: p.ai_retainer_6 },
          { value: "12", label: "12 clips/mo", clips: 12, monthly: p.ai_retainer_12 },
          { value: "18", label: "18 clips/mo", clips: 18, monthly: p.ai_retainer_18 },
        ]},
      { id: "ai_enhance", label: "Advanced Render Enhancement", price: p.ai_enhance, icon: Image, type: "enhancement_block", defaultQty: 3,
        subtitle: "AI-Driven Enhancement Pipeline + Precision Photoshop + Cinematic Post-Production",
        desc: "Each image undergoes a multi-step enhancement process combining advanced AI upscaling, material refinement, lighting adjustments, and detailed Photoshop post-production to elevate realism, sharpness, and overall visual impact.",
        process: [
          "AI-based super resolution & detail reconstruction",
          "Texture refinement & material clarity adjustments",
          "Lighting balance & dynamic range optimization",
          "Atmospheric depth & environmental enhancements",
          "Edge sharpening & artifact cleanup",
          "Final Photoshop polish and color grading"
        ],
        disclaimer: "Delivered as final enhanced output — no revision rounds included.",
        salesTip: "This isn't just AI — we're running your images through an enhancement pipeline that combines AI processing with manual post-production to significantly improve realism and presentation quality.",
        salesShort: "It's the difference between a render and a polished marketing image." },
    ]
  }
];

// ── Reusable Toggle ──
function Toggle({ enabled, onChange, testId }) {
  return (
    <button onClick={() => onChange(!enabled)}
      className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${enabled ? "bg-white" : "bg-white/10"}`}
      data-testid={testId}>
      <div className={`absolute top-0.5 w-4 h-4 rounded-full transition-transform ${enabled ? "bg-black translate-x-4" : "bg-white translate-x-0.5"}`} />
    </button>
  );
}

// ── Summary Row ──
function SummaryRow({ label, value, sublabel, testId, muted, highlight }) {
  return (
    <div className={`flex items-center justify-between py-3 ${muted ? "opacity-50" : ""}`} data-testid={testId}>
      <div>
        <span className={`text-sm ${highlight ? "text-white/70 font-medium" : "text-white/50"}`}>{label}</span>
        {sublabel && <span className="text-xs text-white/25 ml-2">({sublabel})</span>}
      </div>
      <span className={`text-sm font-semibold tabular-nums ${highlight ? "text-white" : "text-white/80"}`}>{value}</span>
    </div>
  );
}

// ══════════════════════════════════════════════
export default function MarketingCalculator() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = quoteId === "new";

  // Dynamic pricing from backend
  const [prices, setPrices] = useState(DEFAULT_PRICES);

  // Mode
  const [mode, setMode] = useState("pre_sale");

  // Core inputs
  const [selectedTier, setSelectedTier] = useState("t3");
  const [units, setUnits] = useState(50);
  const [timeline, setTimeline] = useState(3);
  const [conversionRate, setConversionRate] = useState(1.5);

  // View
  const [viewMode, setViewMode] = useState("monthly");
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  // Infrastructure & Collateral
  const [infraExpanded, setInfraExpanded] = useState(false);
  const [infraToggles, setInfraToggles] = useState({
    landing_page: false, full_website: false,
    branding: false, investment_presentation: false,
    flipbook: false, unit_inventory: false,
    ai_enhance: false,
  });
  const [billboardOption, setBillboardOption] = useState("none");
  const [aiRetainerTier, setAiRetainerTier] = useState("none");
  const [aiEnhanceQty, setAiEnhanceQty] = useState(3);

  // Tooltip
  const [activeTooltip, setActiveTooltip] = useState(null);

  // Quote meta
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [saving, setSaving] = useState(false);
  const [existingQuoteId, setExistingQuoteId] = useState(null);

  // Toggle handler with mutual exclusion
  const handleInfraToggle = (id, val) => {
    const cats = buildInfraCategories(prices);
    const item = cats.flatMap(c => c.items).find(i => i.id === id);
    const next = { ...infraToggles, [id]: val };
    if (val && item?.excludes) next[item.excludes] = false;
    setInfraToggles(next);
  };

  // Fetch pricing from backend
  useEffect(() => {
    apiClient.get("/calculators/marketing-pricing").then(res => {
      if (res.data && Object.keys(res.data).length > 0) {
        setPrices(prev => ({ ...prev, ...res.data }));
      }
    }).catch(() => {});
  }, []);

  // Reset on mode switch
  useEffect(() => {
    setSelectedTier("t3");
    setUnits(50);
    setTimeline(3);
    setConversionRate(mode === "pre_lease" ? 3 : 1.5);
  }, [mode]);

  // Load existing quote
  useEffect(() => {
    if (!isNew && quoteId) {
      apiClient.get(`/calculators/quotes/${quoteId}`).then(res => {
        const q = res.data;
        if (q.metadata?.marketing) {
          const m = q.metadata.marketing;
          setMode(m.mode || "pre_sale");
          setSelectedTier(m.selected_tier || "t3");
          setUnits(m.units || 50);
          setTimeline(m.timeline || 3);
          setConversionRate(m.conversion_rate || 1.5);
          if (m.infra_toggles) setInfraToggles(prev => ({ ...prev, ...m.infra_toggles }));
          if (m.billboard_option) setBillboardOption(m.billboard_option);
          if (m.ai_retainer_tier) setAiRetainerTier(m.ai_retainer_tier);
          if (m.ai_enhance_qty) setAiEnhanceQty(m.ai_enhance_qty);
        }
        setProjectName(q.project_name || "");
        setClientName(q.client_name || "");
        setExistingQuoteId(q.id);
      }).catch(() => toast.error("Failed to load quote"));
    }
  }, [quoteId, isNew]);

  const tiers = mode === "pre_lease" ? buildPreLeaseTiers(prices) : buildPreSaleTiers(prices);
  const currentTier = tiers.find(t => t.id === selectedTier) || tiers[2];
  const convMin = mode === "pre_lease" ? 1.5 : 1;
  const convMax = mode === "pre_lease" ? 8 : 5;

  // Build infra categories from dynamic pricing
  const infraCategories = useMemo(() => buildInfraCategories(prices), [prices]);

  // ── Calculations ──
  const calc = useMemo(() => {
    const SETUP_FEE = prices.setup_fee;
    const MONTHLY_SERVICE_FEE = prices.monthly_service_fee;
    const cpl = currentTier.cpl;
    const rate = conversionRate / 100;
    const totalLeadsRequired = Math.ceil(units / rate);
    const monthlyLeads = Math.ceil(totalLeadsRequired / timeline);
    const monthlyAdSpend = Math.round(monthlyLeads * cpl);
    const monthlyBudget = monthlyAdSpend + MONTHLY_SERVICE_FEE;
    const totalCampaignAdSpend = monthlyAdSpend * timeline;
    const totalCampaignServiceFee = MONTHLY_SERVICE_FEE * timeline;

    // Infrastructure total
    let infraTotal = 0;
    const infraItems = [];

    if (infraToggles.landing_page) { infraTotal += prices.landing_page; infraItems.push({ label: "Landing Page", amount: prices.landing_page }); }
    if (infraToggles.full_website) { infraTotal += prices.full_website; infraItems.push({ label: "Full Website", amount: prices.full_website }); }
    if (infraToggles.branding) { infraTotal += prices.branding; infraItems.push({ label: "Branding & Conceptual Package", amount: prices.branding }); }
    if (infraToggles.investment_presentation) { infraTotal += prices.investment_presentation; infraItems.push({ label: "Investment Presentation", amount: prices.investment_presentation }); }
    if (infraToggles.flipbook) { infraTotal += prices.flipbook; infraItems.push({ label: "Development Flipbook", amount: prices.flipbook }); }

    const billboardPrice = billboardOption === "static" ? prices.billboard_static : billboardOption === "animated" ? prices.billboard_animated : 0;
    if (billboardPrice > 0) { infraTotal += billboardPrice; infraItems.push({ label: `Billboard Design (${billboardOption === "static" ? "Static" : "Animated"})`, amount: billboardPrice }); }

    if (infraToggles.unit_inventory) { infraTotal += prices.unit_inventory; infraItems.push({ label: "Unit Inventory Breakdown Sheet", amount: prices.unit_inventory }); }

    // Advanced Render Enhancement (one-time)
    const aiEnhanceTotal = infraToggles.ai_enhance ? aiEnhanceQty * prices.ai_enhance : 0;
    if (aiEnhanceTotal > 0) { infraTotal += aiEnhanceTotal; infraItems.push({ label: `Advanced Render Enhancement (${aiEnhanceQty} images)`, amount: aiEnhanceTotal }); }

    // AI Social Media Content Retainer (monthly recurring)
    const retainerTierPrices = { "6": prices.ai_retainer_6, "12": prices.ai_retainer_12, "18": prices.ai_retainer_18 };
    const aiRetainerMonthly = aiRetainerTier !== "none" ? (retainerTierPrices[aiRetainerTier] || 0) : 0;
    const aiRetainerCampaignTotal = aiRetainerMonthly * timeline;

    const totalCampaignCost = SETUP_FEE + totalCampaignAdSpend + totalCampaignServiceFee + infraTotal + aiRetainerCampaignTotal;
    const estimatedInitialInvestment = SETUP_FEE + monthlyBudget + infraTotal + aiRetainerMonthly;

    return {
      cpl, totalLeadsRequired, monthlyLeads, monthlyAdSpend,
      monthlyBudget, totalCampaignAdSpend, totalCampaignServiceFee, totalCampaignCost,
      infraTotal, infraItems, aiEnhanceTotal,
      aiRetainerMonthly, aiRetainerCampaignTotal,
      estimatedInitialInvestment, SETUP_FEE, MONTHLY_SERVICE_FEE
    };
  }, [currentTier, units, timeline, conversionRate, prices, infraToggles, billboardOption, aiRetainerTier, aiEnhanceQty]);

  // ── Build Proposal ──
  const buildSections = () => {
    const modeLabel = mode === "pre_lease" ? "Pre-Lease" : "Pre-Sale";
    const desc = mode === "pre_lease"
      ? "This budget recommendation is designed to support a targeted leasing campaign based on the project's rental profile, unit count, lease-up timeline, and estimated lead-to-lease conversion rate."
      : "This budget recommendation is designed to support a targeted pre-sale campaign based on the project's price point, inventory count, sell-out timeline, and estimated lead-to-sale conversion rate.";

    const sections = [{
      id: crypto.randomUUID(),
      section_type: "marketing_strategy",
      label: "Marketing Strategy & Lead Generation",
      line_items: [
        { id: crypto.randomUUID(), label: "Setup Fee", quantity: 1, rate: calc.SETUP_FEE, subtotal: calc.SETUP_FEE, unit_type: "one-time" },
        { id: crypto.randomUUID(), label: "Monthly Service Fee", quantity: timeline, rate: calc.MONTHLY_SERVICE_FEE, subtotal: calc.MONTHLY_SERVICE_FEE * timeline, unit_type: "monthly" },
        { id: crypto.randomUUID(), label: "Recommended Monthly Ad Spend", quantity: timeline, rate: calc.monthlyAdSpend, subtotal: calc.totalCampaignAdSpend, unit_type: "monthly" },
      ],
      subtotal: calc.SETUP_FEE + calc.totalCampaignAdSpend + calc.totalCampaignServiceFee,
      metadata: { mode, mode_label: modeLabel, cpl: calc.cpl, units, timeline, conversion_rate: conversionRate, monthly_leads: calc.monthlyLeads, total_leads: calc.totalLeadsRequired, monthly_budget: calc.monthlyBudget, client_description: desc }
    }];

    if (calc.infraTotal > 0 || calc.aiRetainerMonthly > 0) {
      const infraLineItems = calc.infraItems.map(item => ({
        id: crypto.randomUUID(),
        label: item.label,
        quantity: 1,
        rate: item.amount,
        subtotal: item.amount,
        unit_type: item.label.includes("Render Enhancement") ? "ai_content" : "one-time"
      }));
      if (calc.aiRetainerMonthly > 0) {
        infraLineItems.push({
          id: crypto.randomUUID(),
          label: `AI Social Media Content (${aiRetainerTier} clips/mo)`,
          quantity: timeline,
          rate: calc.aiRetainerMonthly,
          subtotal: calc.aiRetainerCampaignTotal,
          unit_type: "monthly_retainer"
        });
      }
      sections.push({
        id: crypto.randomUUID(),
        section_type: "marketing_infrastructure",
        label: "Marketing Infrastructure & Collateral",
        line_items: infraLineItems,
        subtotal: calc.infraTotal + calc.aiRetainerCampaignTotal,
        metadata: {
          description: "These services support the overall marketing campaign by ensuring the project is properly positioned, presented, and optimized for conversion across all channels.",
          ai_disclaimer: (calc.aiEnhanceTotal > 0 || calc.aiRetainerMonthly > 0) ? "AI-enhanced content is delivered as final output and does not include revision rounds." : null
        }
      });
    }

    return sections;
  };

  const handleSave = async () => {
    if (!projectName.trim()) { toast.error("Enter a project name"); return; }
    setSaving(true);
    try {
      const payload = {
        project_name: projectName,
        client_name: clientName,
        salesperson: user?.name || "",
        quote_type: "marketing",
        sections: buildSections(),
        metadata: {
          marketing: {
            mode, selected_tier: selectedTier, units, timeline,
            conversion_rate: conversionRate,
            infra_toggles: infraToggles,
            billboard_option: billboardOption,
            ai_retainer_tier: aiRetainerTier,
            ai_enhance_qty: aiEnhanceQty,
          }
        },
        total_one_time: calc.SETUP_FEE + calc.infraTotal,
        total_recurring: calc.monthlyBudget,
      };
      if (existingQuoteId && !isNew) {
        await apiClient.put(`/calculators/quotes/${existingQuoteId}`, payload);
        toast.success("Quote updated");
      } else {
        const res = await apiClient.post("/calculators/quotes", payload);
        toast.success("Quote saved");
        navigate(`/sales/calculators/marketing/${res.data.id}`, { replace: true });
      }
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const modeLabel = mode === "pre_lease" ? "Pre-Lease" : "Pre-Sale";
  const unitLabel = mode === "pre_lease" ? "Lease Out" : "Sell Out";

  // ══════════ RENDER ══════════
  return (
    <div className="min-h-screen bg-[#000000] text-white selection:bg-white/10 selection:text-white" data-testid="marketing-calculator">
      {/* Top Bar */}
      <div className="border-b border-white/[0.06] px-8 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/sales/calculators")} className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm" data-testid="marketing-back-btn">
          <ChevronLeft className="w-4 h-4" /> Calculators
        </button>
        <div className="flex items-center gap-3">
          <input value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="Project Name"
            className="bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-white/30 w-48 focus:outline-none focus:border-white/20" data-testid="marketing-project-name" />
          <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Client"
            className="bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-white/30 w-36 focus:outline-none focus:border-white/20" data-testid="marketing-client-name" />
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 bg-white text-black px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-white/90 transition-colors disabled:opacity-50" data-testid="marketing-save-btn">
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-10">
        {/* Title */}
        <div className="mb-10">
          <p className="text-white/40 text-sm font-medium tracking-wider uppercase mb-2">Marketing Calculator</p>
          <h1 className="text-4xl font-light tracking-tight">{mode === "pre_lease" ? "Pre-Lease Calculator" : "Pre-Sale Calculator"}</h1>
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-1 p-1 bg-white/[0.06] rounded-xl w-fit mb-12" data-testid="marketing-mode-toggle">
          {[{ key: "pre_lease", label: "Pre-Lease" }, { key: "pre_sale", label: "Pre-Sale" }].map(m => (
            <button key={m.key} onClick={() => setMode(m.key)}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${mode === m.key ? "bg-white text-black shadow-lg" : "text-white/50 hover:text-white/80"}`}
              data-testid={`marketing-mode-${m.key}`}>{m.label}</button>
          ))}
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-12 gap-12">
          {/* ═══ Left Column ═══ */}
          <div className="col-span-7 space-y-10">

            {/* Tier Selection */}
            <div>
              <label className="block text-sm font-medium text-white/60 mb-4">
                {mode === "pre_lease" ? "Average Rent Per Unit" : "Average Price Per Unit"}
              </label>
              <div className="grid grid-cols-2 gap-3">
                {tiers.map(tier => (
                  <button key={tier.id} onClick={() => setSelectedTier(tier.id)}
                    className={`relative text-left p-4 rounded-xl border transition-all duration-200 ${selectedTier === tier.id
                      ? "border-white/30 bg-white/[0.08] ring-1 ring-white/20"
                      : "border-white/[0.08] bg-white/[0.03] hover:border-white/[0.15] hover:bg-white/[0.05]"}`}
                    data-testid={`marketing-tier-${tier.id}`}>
                    <div className="font-semibold text-[15px] text-white">{tier.label}</div>
                    <div className="text-xs text-white/40 mt-1">{tier.sublabel}</div>
                    {selectedTier === tier.id && <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-white" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Units Slider */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-white/60">How Many Units?</label>
                <div className="bg-white/[0.08] border border-white/[0.1] rounded-lg px-3 py-1 text-sm font-semibold tabular-nums" data-testid="marketing-units-display">{units} units</div>
              </div>
              <Slider value={[units]} onValueChange={([v]) => setUnits(v)} min={4} max={1000} step={1}
                className="[&_[data-slot=track]]:bg-white/10 [&_[data-slot=range]]:bg-white [&_[data-slot=thumb]]:bg-white [&_[data-slot=thumb]]:border-0 [&_[data-slot=thumb]]:w-5 [&_[data-slot=thumb]]:h-5 [&>span>span]:bg-white/10 [&>span>span>span]:bg-white [&_button]:bg-white [&_button]:border-0 [&_button]:w-5 [&_button]:h-5"
                data-testid="marketing-units-slider" />
              <div className="flex justify-between text-xs text-white/30 mt-2"><span>4 units</span><span>1,000 units</span></div>
            </div>

            {/* Timeline Slider */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-white/60">{unitLabel} Target Timeline</label>
                <div className="bg-white/[0.08] border border-white/[0.1] rounded-lg px-3 py-1 text-sm font-semibold tabular-nums" data-testid="marketing-timeline-display">{timeline} months</div>
              </div>
              <Slider value={[timeline]} onValueChange={([v]) => setTimeline(v)} min={3} max={18} step={1}
                className="[&>span>span]:bg-white/10 [&>span>span>span]:bg-white [&_button]:bg-white [&_button]:border-0 [&_button]:w-5 [&_button]:h-5"
                data-testid="marketing-timeline-slider" />
              <div className="flex justify-between text-xs text-white/30 mt-2"><span>3 months</span><span>18 months</span></div>
            </div>

            {/* Conversion Rate */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-white/60">Sales Team Conversion Rate</label>
                <div className="bg-white/[0.08] border border-white/[0.1] rounded-lg px-3 py-1 text-sm font-semibold tabular-nums" data-testid="marketing-conv-display">{conversionRate}%</div>
              </div>
              <Slider value={[conversionRate * 10]} onValueChange={([v]) => setConversionRate(v / 10)} min={convMin * 10} max={convMax * 10} step={5}
                className="[&>span>span]:bg-white/10 [&>span>span>span]:bg-white [&_button]:bg-white [&_button]:border-0 [&_button]:w-5 [&_button]:h-5"
                data-testid="marketing-conv-slider" />
              <div className="flex justify-between text-xs text-white/30 mt-2"><span>{convMin}%</span><span>{convMax}%</span></div>
            </div>

            {/* ═══ Marketing Infrastructure & Collateral ═══ */}
            <div className="border-t border-white/[0.06] pt-6">
              <button onClick={() => setInfraExpanded(!infraExpanded)}
                className="w-full flex items-center justify-between group" data-testid="infra-section-toggle">
                <div className="flex items-center gap-2.5">
                  <Sparkles className="w-4 h-4 text-white/40" />
                  <h3 className="text-sm font-semibold text-white/80">Marketing Infrastructure & Collateral</h3>
                  {(calc.infraTotal > 0 || calc.aiRetainerMonthly > 0) && (
                    <span className="text-[10px] font-semibold text-white/50 tabular-nums">
                      {calc.infraTotal > 0 ? fmt(calc.infraTotal) : ""}{calc.infraTotal > 0 && calc.aiRetainerMonthly > 0 ? " + " : ""}{calc.aiRetainerMonthly > 0 ? `${fmt(calc.aiRetainerMonthly)}/mo` : ""}
                    </span>
                  )}
                </div>
                {infraExpanded ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
              </button>

              {infraExpanded && (
                <div className="mt-6 space-y-8" data-testid="infra-section-content">
                  {infraCategories.map(cat => (
                    <div key={cat.id}>
                      <p className={`text-[10px] font-bold tracking-wider uppercase mb-4 ${
                        cat.color === "sky" ? "text-sky-400/60" :
                        cat.color === "violet" ? "text-violet-400/60" :
                        cat.color === "amber" ? "text-amber-400/60" :
                        "text-rose-400/60"
                      }`}>{cat.label}</p>

                      <div className="space-y-1">
                        {cat.items.map(item => {
                          const Icon = item.icon;
                          const isEnabled = item.type === "dropdown" ? billboardOption !== "none"
                            : item.type === "retainer" ? aiRetainerTier !== "none"
                            : item.type === "enhancement_block" ? infraToggles[item.id]
                            : infraToggles[item.id];
                          const isDisabled = item.excludes && infraToggles[item.excludes];

                          return (
                            <div key={item.id} className={`${isDisabled ? "opacity-30 pointer-events-none" : ""}`}>
                              {/* Retainer package selector */}
                              {item.type === "retainer" ? (
                                <div className="py-3">
                                  <div className="flex items-center gap-3 mb-3">
                                    <Icon className={`w-4 h-4 flex-shrink-0 ${isEnabled ? "text-white/60" : "text-white/20"}`} />
                                    <div className="flex items-center gap-2 flex-1">
                                      <span className={`text-sm ${isEnabled ? "text-white/80" : "text-white/50"}`}>{item.label}</span>
                                      <span className="text-[10px] text-white/25">{item.subtext}</span>
                                      <button onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === item.id ? null : item.id); }}
                                        className="text-white/20 hover:text-white/40 transition-colors" data-testid={`infra-info-${item.id}`}>
                                        <HelpCircle className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                  {activeTooltip === item.id && (
                                    <p className="text-[11px] text-white/30 ml-7 mb-3 leading-relaxed">{item.desc}</p>
                                  )}
                                  <div className="grid grid-cols-4 gap-2 ml-7" data-testid="ai-retainer-selector">
                                    {item.tiers.map(tier => (
                                      <button key={tier.value} onClick={() => setAiRetainerTier(tier.value)}
                                        className={`text-center py-2.5 px-2 rounded-xl border transition-all ${
                                          aiRetainerTier === tier.value
                                            ? "border-white/30 bg-white/[0.08]"
                                            : "border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12] hover:bg-white/[0.04]"
                                        }`}
                                        data-testid={`ai-retainer-tier-${tier.value}`}>
                                        <div className={`text-xs font-semibold ${aiRetainerTier === tier.value ? "text-white" : "text-white/50"}`}>
                                          {tier.label}
                                        </div>
                                        {tier.monthly > 0 && (
                                          <div className={`text-[10px] mt-0.5 tabular-nums ${aiRetainerTier === tier.value ? "text-white/60" : "text-white/25"}`}>
                                            {fmt(tier.monthly)}/mo
                                          </div>
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                              ) : item.type === "enhancement_block" ? (
                                /* ═══ Advanced Render Enhancement — Premium Block ═══ */
                                <div className="py-4 mt-2 border-t border-white/[0.04]" data-testid="advanced-render-enhancement">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2.5">
                                        <Icon className={`w-4 h-4 flex-shrink-0 ${isEnabled ? "text-white/70" : "text-white/20"}`} />
                                        <span className={`text-sm font-semibold ${isEnabled ? "text-white" : "text-white/50"}`}>{item.label}</span>
                                        <span className="text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full border border-rose-500/20 text-rose-300/70">
                                          Marketing Optimization Ready
                                        </span>
                                      </div>
                                      <p className="text-[11px] text-white/30 mt-1.5 ml-[26px] leading-relaxed tracking-wide">
                                        {item.subtitle}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-3 ml-4 mt-0.5">
                                      <span className="text-xs text-white/30 tabular-nums">{fmt(item.price)}/image</span>
                                      <Toggle enabled={infraToggles[item.id]} onChange={v => handleInfraToggle(item.id, v)} testId={`infra-toggle-${item.id}`} />
                                    </div>
                                  </div>

                                  {infraToggles[item.id] && (
                                    <div className="ml-[26px] mt-4 space-y-4">
                                      {/* Description */}
                                      <p className="text-[11px] text-white/25 leading-relaxed">
                                        {item.desc}
                                      </p>

                                      {/* Technical Process */}
                                      <div>
                                        <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2.5">Enhancement Process</p>
                                        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                                          {item.process.map((step, i) => (
                                            <div key={i} className="flex items-start gap-2">
                                              <Zap className="w-3 h-3 mt-0.5 text-rose-400/40 flex-shrink-0" />
                                              <span className="text-[11px] text-white/35 leading-snug">{step}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>

                                      {/* Quantity + Subtotal */}
                                      <div className="flex items-center gap-4 pt-2">
                                        <div className="flex items-center gap-1">
                                          <button onClick={() => setAiEnhanceQty(Math.max(1, aiEnhanceQty - 1))}
                                            className="w-7 h-7 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center hover:bg-white/[0.1]"
                                            data-testid="infra-ai_enhance-minus"><Minus className="w-3 h-3 text-white/60" /></button>
                                          <span className="w-10 text-center text-sm font-semibold tabular-nums" data-testid="infra-ai_enhance-qty">
                                            {aiEnhanceQty}
                                          </span>
                                          <button onClick={() => setAiEnhanceQty(aiEnhanceQty + 1)}
                                            className="w-7 h-7 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center hover:bg-white/[0.1]"
                                            data-testid="infra-ai_enhance-plus"><Plus className="w-3 h-3 text-white/60" /></button>
                                        </div>
                                        <span className="text-[10px] text-white/25">images</span>
                                        <span className="text-sm font-semibold text-white/60 ml-auto tabular-nums" data-testid="infra-ai_enhance-subtotal">
                                          {fmt(aiEnhanceQty * item.price)}
                                        </span>
                                      </div>

                                      {/* Disclaimer */}
                                      <p className="text-[10px] text-white/15 italic">
                                        {item.disclaimer}
                                      </p>

                                      {/* Sales Positioning */}
                                      <button onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === "enhance_sales" ? null : "enhance_sales"); }}
                                        className="flex items-center gap-1.5 text-white/15 hover:text-white/30 transition-colors"
                                        data-testid="enhance-sales-tip-toggle">
                                        <HelpCircle className="w-3 h-3" />
                                        <span className="text-[10px]">Sales Positioning</span>
                                      </button>
                                      {activeTooltip === "enhance_sales" && (
                                        <div className="space-y-2">
                                          <p className="text-[11px] text-white/20 leading-relaxed">
                                            {item.salesTip}
                                          </p>
                                          <p className="text-[10px] text-white/15 italic">
                                            "{item.salesShort}"
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>

                              ) : (
                                /* Standard toggle / dropdown items */
                                <div className="flex items-center justify-between py-3">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <Icon className={`w-4 h-4 flex-shrink-0 ${isEnabled ? "text-white/60" : "text-white/20"}`} />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className={`text-sm ${isEnabled ? "text-white/80" : "text-white/50"}`}>{item.label}</span>
                                        {item.subtext && <span className="text-[10px] text-white/25">{item.subtext}</span>}
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === item.id ? null : item.id); }}
                                          className="text-white/20 hover:text-white/40 transition-colors"
                                          data-testid={`infra-info-${item.id}`}>
                                          <HelpCircle className="w-3 h-3" />
                                        </button>
                                      </div>
                                      {activeTooltip === item.id && (
                                        <p className="text-[11px] text-white/30 mt-1 leading-relaxed">{item.desc}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3 ml-4">
                                    {item.type === "toggle" && (
                                      <>
                                        <span className="text-xs text-white/30 tabular-nums">{fmt(item.price)}</span>
                                        <Toggle enabled={infraToggles[item.id]} onChange={v => handleInfraToggle(item.id, v)} testId={`infra-toggle-${item.id}`} />
                                      </>
                                    )}
                                    {item.type === "dropdown" && (
                                      <select value={billboardOption} onChange={e => setBillboardOption(e.target.value)}
                                        className="bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-white/20 appearance-none cursor-pointer"
                                        data-testid="infra-billboard-select">
                                        {item.options.map(opt => (
                                          <option key={opt.value} value={opt.value} className="bg-[#000000] text-white">
                                            {opt.label}{opt.price > 0 ? ` — ${fmt(opt.price)}` : ""}
                                          </option>
                                        ))}
                                      </select>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Infra Total */}
                  {(calc.infraTotal > 0 || calc.aiRetainerMonthly > 0) && (
                    <div className="pt-4 border-t border-white/[0.06] space-y-2">
                      {calc.infraTotal > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white/50">One-time Services</span>
                          <span className="text-sm font-semibold text-white tabular-nums" data-testid="infra-onetime-total">{fmt(calc.infraTotal)}</span>
                        </div>
                      )}
                      {calc.aiRetainerMonthly > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white/50">AI Content Retainer</span>
                          <span className="text-sm font-semibold text-white tabular-nums" data-testid="infra-retainer-monthly">{fmt(calc.aiRetainerMonthly)}/mo</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* How It Works */}
            <div className="border-t border-white/[0.06] pt-4">
              <button onClick={() => setShowHowItWorks(!showHowItWorks)}
                className="flex items-center gap-2 text-sm font-medium text-white/50 hover:text-white transition-colors" data-testid="marketing-how-it-works-toggle">
                {showHowItWorks ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                How It Works
              </button>
              {showHowItWorks && (
                <div className="mt-6 space-y-4">
                  {HOW_IT_WORKS.map(step => (
                    <div key={step.step} className="bg-white/[0.04] border border-white/[0.06] rounded-2xl p-6" data-testid={`hiw-step-${step.step}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-white/[0.08] text-xs font-bold text-white/70">{step.step}</span>
                        <h3 className="font-semibold text-white">{step.title}</h3>
                      </div>
                      <p className="text-sm text-white/50 leading-relaxed mb-4">{step.body}</p>
                      <ul className="space-y-2">
                        {step.bullets.map((b, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-white/40">
                            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-white/20 flex-shrink-0" />{b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ═══ Right Column — Premium Summary Card ═══ */}
          <div className="col-span-5">
            <div className="sticky top-8">
              <div className="border border-white/[0.08] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] bg-[#000000] rounded-[24px] overflow-hidden" data-testid="marketing-summary-card">
                <div className="flex items-center justify-between gap-4 px-6 pt-6 pb-4 border-b border-white/[0.06]">
                  <h2 className="text-[17px] font-light text-white tracking-tight leading-tight">Quote Summary</h2>
                  <span className="text-[10px] uppercase tracking-[0.12em] text-white/35">Marketing Strategy</span>
                </div>
                <div className="px-6 pt-6 pb-8 select-none">
                  {/* View Toggle — premium segmented control */}
                  <div className="flex items-center bg-white/[0.04] border border-white/[0.06] rounded-[10px] p-0.5 mb-6 w-fit">
                    {[{ key: "monthly", label: "Monthly" }, { key: "campaign", label: "Full Campaign" }].map(v => (
                      <button key={v.key} onClick={() => setViewMode(v.key)}
                        className={`px-3 py-1.5 rounded-[8px] text-[11px] font-medium tracking-wide transition-all duration-200 ${viewMode === v.key ? "bg-white/[0.08] text-white shadow-sm" : "text-white/50 hover:text-white/75"}`}
                        data-testid={`marketing-view-${v.key}`}>{v.label}</button>
                    ))}
                  </div>

                  {/* Headline */}
                  <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-2">
                    {viewMode === "monthly" ? "Recommended Monthly Budget" : "Total Campaign Investment"}
                  </p>
                  <p className="text-[40px] font-light tracking-tight text-white leading-none mb-8 tabular-nums" data-testid="marketing-headline-value">
                    {viewMode === "monthly" ? fmt(calc.monthlyBudget) : fmt(calc.totalCampaignCost)}
                  </p>

                  {/* Breakdown */}
                  <div>
                    <SummaryRow label="Set Up Fee" value={fmt(calc.SETUP_FEE)} testId="summary-setup-fee" muted={viewMode === "monthly"} />
                    <SummaryRow label="Monthly Service Fee"
                      value={viewMode === "monthly" ? fmt(calc.MONTHLY_SERVICE_FEE) : fmt(calc.totalCampaignServiceFee)}
                      sublabel={viewMode === "campaign" ? `${timeline} months` : null} testId="summary-service-fee" />
                    <SummaryRow label="Monthly Ad Spend"
                      value={viewMode === "monthly" ? fmt(calc.monthlyAdSpend) : fmt(calc.totalCampaignAdSpend)}
                      sublabel={viewMode === "campaign" ? `${timeline} months` : null} testId="summary-ad-spend" />

                    {calc.infraTotal > 0 && (
                      <SummaryRow label="Additional Marketing Services" value={fmt(calc.infraTotal)} testId="summary-infra-total" />
                    )}

                    {calc.aiRetainerMonthly > 0 && (
                      <SummaryRow label={`AI Content Retainer (${aiRetainerTier} clips/mo)`}
                        value={viewMode === "monthly" ? fmt(calc.aiRetainerMonthly) : fmt(calc.aiRetainerCampaignTotal)}
                        sublabel={viewMode === "campaign" ? `${timeline} months` : "monthly"}
                        testId="summary-ai-retainer" />
                    )}

                    <div className="h-px bg-white/[0.06] my-2" />

                    <SummaryRow label="Est. Monthly Leads Generated" value={calc.monthlyLeads.toLocaleString()} testId="summary-monthly-leads" highlight />
                    <SummaryRow label="Total Est. Leads Generated" value={calc.totalLeadsRequired.toLocaleString()} testId="summary-total-leads" highlight />
                  </div>

                  {(calc.infraTotal > 0 || calc.aiRetainerMonthly > 0) && (
                    <div className="mt-5 pt-5 border-t border-white/[0.08]">
                      <div className="p-4 bg-[#000000] border border-white/[0.08] rounded-[16px]">
                        <div className="flex items-baseline justify-between" data-testid="summary-initial-investment">
                          <span className="text-[11px] uppercase tracking-[0.1em] text-white/50">Estimated Initial Investment</span>
                          <span className="text-[22px] font-light text-white tabular-nums tracking-tight">{fmt(calc.estimatedInitialInvestment)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="mt-6 text-[11px] text-white/30 leading-relaxed">
                    Based on {modeLabel} CPL of {fmt(currentTier.cpl)} per lead, {conversionRate}% conversion rate, and {timeline}-month timeline for {units} units.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
